/**
 * Article results on the search page (E20-1262).
 *
 * Shopify blends products, pages and articles into one relevance-ranked list and applies a
 * cutoff, so a query matching 46 articles rendered exactly one of them — including titles that
 * matched the term outright. Liquid can only see one search per render, so the only way to get
 * the real set is a second request scoped with `type=article`.
 *
 * This fetches `sections/search-articles.liquid` through the Section Rendering API and swaps it
 * in for the blended fallback that `collection-grid.liquid` rendered server-side. Progressive
 * enhancement: with the script blocked, that fallback stays exactly as it is today.
 *
 * Singleton — one search page, one article section — so a closure rather than a custom element.
 */
const SECTION_ID = 'search-articles';
const TARGET_SELECTOR = '[data-search-articles]';
// The "no matches" state collection-grid renders when the blend left nothing visible.
const EMPTY_SELECTOR = '[data-search-empty]';

const loadSearchArticles = async () => {
  const target = document.querySelector(TARGET_SELECTOR);
  if (!target) return;

  const terms = target.dataset.terms?.trim();
  // No query means nothing to scope the second request to; leave the fallback alone.
  if (!terms) return;

  const endpoint = window.theme?.routes?.search;
  // No route means the theme globals never initialised — the fallback is still on the page.
  if (!endpoint) return;

  const params = new URLSearchParams({
    q: terms,
    type: 'article',
    section_id: SECTION_ID,
  });

  try {
    const response = await fetch(`${endpoint}?${params}`);
    if (!response.ok) throw new Error(`Article search responded ${response.status}`);

    const markup = new DOMParser()
      .parseFromString(await response.text(), 'text/html')
      .querySelector(`#shopify-section-${SECTION_ID}`);
    if (!markup) throw new Error('Article section missing from the response');

    const section = markup.querySelector('.search-results__section');

    // A scoped search can legitimately match no articles even when the blend surfaced one, since
    // the two use different result sets. Drop the stale fallback rather than leaving it to
    // contradict the real answer.
    if (!section) {
      target.remove();
      return;
    }

    // Source is Shopify's own section render, so this markup is trusted.
    target.replaceWith(section);
    // The blend can surface only posts hidden in this market while the scoped search finds
    // visible ones, so a "no matches" state rendered beside the target is now wrong.
    document.querySelector(EMPTY_SELECTOR)?.remove();
  } catch (error) {
    // Don't fail silently, but don't tear down the fallback either — a visible single article
    // beats an empty gap where the section was.
    console.error('[bfx-search-articles] could not load scoped article results', error);
  }
};

loadSearchArticles();
